const PGAdapter = require('./../../common/pg-adapter')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')
const { SHIPMENT_STATUS } = require('../constants')
const { buildOtp } = require('../utils/utils')

const DATA_SHIPMENTSNAPSHOT_TABLE_NAME = 'avocado.data_shipmentsnapshot'
const DATA_SHIPMENTSNAPSHOT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'origin_id',
  'destination_id',
  'notes',
  'is_automatic_return',
  'parent_doc_id',
  'order_id',
  'created_at',
  'updated_at',
  'created_by',
  'couchdb_id',
  'suborder_id',
  'is_gps_authenticated',
  'gps_validation_fail_comment',
  'gps_validation',
  'raw_order_id'
]

const DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME = 'avocado.data_shipmentsnapshot_change'
const DATA_SHIPMENT_COMMENT_TABLE_NAME = 'avocado.data_shipment_comment'

// Fallback PG statement budget when the caller does not pass one (local
// runners, direct API use). Matches the route-shipment-cache Lambda's 120 s
// timeout minus a 10 s margin; the Lambda itself derives and passes its real
// remaining budget per invocation (see handler.ts), so changing the Lambda
// timeout changes the server-side budget automatically. The point of the
// setting: kill the producer query before the Lambda dies, so Postgres never
// keeps executing an orphaned statement after the client is gone (orphans
// were observed burning CPU for up to 20 minutes - issue 25067).
const ROUTE_CACHE_STATEMENT_TIMEOUT_MS = 110000

class ShipmentSnapshotAdapter extends PGAdapter {
  constructor (pgConnection, username, logger, options = {}) {
    super(
      pgConnection,
      DATA_SHIPMENTSNAPSHOT_TABLE_NAME,
      username,
      DATA_SHIPMENTSNAPSHOT_COLUMNS,
      'id',
      null,
      logger,
      { strictColumns: false, withGlobalAccessUser: options.withGlobalAccessUser }
    )
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string (e.g., '2024-01-01T00:00:00.000Z')
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds (origins + destinations)
   */
  async getCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!Array.isArray(routeIds)) {
      throw new Error('routeIds must be an array')
    }
    if (routeIds.length === 0) {
      return { couchdbIds: [], locationIds: [], funderIds: [], serviceIds: [], productIds: [] }
    }

    const { programIds = [], cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let query = `
      SELECT ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.couchdb_id IS NOT NULL
        AND ss.route_id = ANY($${paramIndex++}::text[])
    `

    params.push(routeIds)

    if (programIds.length > 0) {
      query += ` AND ss.program_id = ANY($${paramIndex++}::text[])`
      params.push(programIds)
    }

    if (cutoffDate) {
      query += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    // We add the Group by clause after adding potential filters
    query += ` GROUP BY ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, s.fsid`

    try {
      const result = await this.pgConnection.query(query, params)

      const couchdbIds = result.rows.map(row => row.couchdb_id)
      // Dedupe location IDs because multiple shipments may share the same origin/destination.
      // These IDs are used for offline locationMatch filtering in the van-shipments dispenser.
      const locationSet = new Set()
      const skuSet = []
      const funderSet = new Set()
      const serviceSet = new Set()

      result.rows.forEach(row => {
        if (row.origin_id) locationSet.add(row.origin_id)
        if (row.destination_id) locationSet.add(row.destination_id)
        if (row.skus && Array.isArray(row.skus)) {
          skuSet.push(...row.skus)
        }
        if (row.funder_id) funderSet.add(row.funder_id)
        if (row.service_id) serviceSet.add(row.service_id)
      })

      return {
        couchdbIds,
        locationIds: Array.from(locationSet),
        funderIds: Array.from(funderSet),
        serviceIds: Array.from(serviceSet),
        productIds: [...new Set(skuSet)]
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-query-failed',
          routeIds,
          programIds,
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get CouchDB IDs, location IDs, funder IDs, service IDs, product IDs, and change doc IDs
   * for shipment snapshots grouped by route ID.
   * Aggregates per route on the server and returns one row per route.
   *
   * The whole refresh runs on one pinned client in a single transaction so the
   * statement timeout stays scoped to this refresh:
   *  - SET LOCAL statement_timeout kills the query before the Lambda's own
   *    timeout fires, so a killed invocation never leaves an orphaned
   *    statement burning server CPU (observed up to 20 minutes - issue 25067).
   *    The budget derives from the Lambda's remaining time per invocation.
   *    Overlap between scheduled runs is prevented upstream instead: the
   *    Lambda is limited to one concurrent execution, EventBridge retries are
   *    disabled, and a query killed at the statement timeout dies before the
   *    next 2-minute tick starts.
   *
   * Aggregation formulation notes (production-measured, July 2026):
   *  - services: dedupe (route_id, program_id) BEFORE joining data_program/
   *    data_service. Joining at snapshot granularity fanned out to 878,226
   *    rows (planner est. 8,075) and sorted 82.8 MB on disk; the pre-dedup
   *    form measured 2,720 ms -> 76 ms.
   *  - id arrays use SELECT DISTINCT + array_agg (hash dedup) instead of
   *    ARRAY_AGG(DISTINCT ...) (Sort + GroupAggregate, spills to disk).
   *  - couchdb_ids drops DISTINCT: couchdb_id is unique per snapshot row.
   *  - product_ids keeps ssl.id in the dedup projection: without it the
   *    planner can pick an Index Only Scan on the write-only
   *    "unique shipment_snapshot_id:sku" index, measured 0.28-0.32 ms per
   *    probe (~24-27 s per run vs 0.005 ms via the hot plain index).
   *  - products/batches are separate CTEs so each dedup stays narrow.
   * @param {object} options - Query options
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @param {number} [options.statementTimeoutMs] - PG statement_timeout for the refresh, in ms. The Lambda handler derives this from its own remaining budget; defaults to ROUTE_CACHE_STATEMENT_TIMEOUT_MS
   * @returns {Promise<Object<string, {carrierId: string|null, couchdbIds: string[], locationIds: string[], funderIds: string[], serviceIds: string[], productIds: string[], changeDocIds: string[]}>>} Map of routeId -> route data
   */
  async getCouchdbIdsByRoutesGrouped (options = {}) {
    const { cutoffDate = null, statementTimeoutMs = ROUTE_CACHE_STATEMENT_TIMEOUT_MS } = options

    // Production hands us a pg Pool to pin a client from; test harnesses may
    // pass an already-connected Client instead. Pool-only getters (idleCount,
    // totalCount) tell the two apart without instanceof. The pin happens
    // inside the try so a connect failure lands in the same logged error path
    // as query failures.
    const isPool = typeof this.pgConnection.idleCount === 'number'
    let client
    try {
      client = isPool ? await this.pgConnection.connect() : this.pgConnection
      await client.query('BEGIN')

      await client.query(`SET LOCAL statement_timeout = ${statementTimeoutMs}`)

      // Keep a snapshot in the window if EITHER it OR its parent route was
      // touched recently. `assignCarrier` (and other route-only mutations)
      // bumps `data_route.updated_at` but not the snapshots', so checking
      // only snapshots would silently drop a route's shipments after its
      // snapshots aged out even though the route itself is current.
      // Fetching the recent route ids first (data_route is tiny) keeps the
      // main filter's OR on a single table, where indexes can serve it.
      let params = []
      let cutoffClause = ''
      if (cutoffDate) {
        const recentRoutes = await client.query(
          'SELECT couchdb_id FROM avocado.data_route WHERE updated_at >= $1',
          [cutoffDate]
        )
        params = [cutoffDate, recentRoutes.rows.map(row => row.couchdb_id)]
        cutoffClause = `
            AND (ss.updated_at >= $1 OR ss.route_id = ANY($2::text[]))`
      }

      // The filtered snapshot set is materialized once and shared by every
      // aggregation, so the big snapshot table is scanned a single time.
      // Each follow-up CTE groups per route; the line/details fan-out stays
      // inside the products/batches CTEs and never multiplies the
      // snapshot-level rows.
      const query = `
        WITH snapshots AS MATERIALIZED (
          SELECT ss.id, ss.route_id, ss.couchdb_id, ss.shipment_id,
                 ss.origin_id, ss.destination_id, ss.funder_id,
                 ss.program_id, ss.updated_at, r.carrier_id
          FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
          LEFT JOIN avocado.data_route r ON ss.route_id = r.couchdb_id
          WHERE ss.route_id IS NOT NULL
            AND ss.couchdb_id IS NOT NULL${cutoffClause}
        ),
        routes AS (
          SELECT route_id,
                 MAX(carrier_id::text) AS carrier_id,
                 MAX(updated_at) AS last_updated
          FROM snapshots
          GROUP BY route_id
        ),
        couchdb AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS couchdb_ids
          FROM snapshots
          GROUP BY route_id
        ),
        origins AS (
          SELECT route_id, ARRAY_AGG(origin_id) AS origin_ids
          FROM (SELECT DISTINCT route_id, origin_id FROM snapshots WHERE origin_id IS NOT NULL) d
          GROUP BY route_id
        ),
        destinations AS (
          SELECT route_id, ARRAY_AGG(destination_id) AS destination_ids
          FROM (SELECT DISTINCT route_id, destination_id FROM snapshots WHERE destination_id IS NOT NULL) d
          GROUP BY route_id
        ),
        funders AS (
          SELECT route_id, ARRAY_AGG(funder_id) AS funder_ids
          FROM (SELECT DISTINCT route_id, funder_id FROM snapshots WHERE funder_id IS NOT NULL) d
          GROUP BY route_id
        ),
        services AS (
          SELECT route_id, ARRAY_AGG(fsid) AS service_ids
          FROM (
            SELECT DISTINCT rp.route_id, sv.fsid
            FROM (SELECT DISTINCT route_id, program_id FROM snapshots WHERE program_id IS NOT NULL) rp
            JOIN avocado.data_program p ON rp.program_id = p.fsid
            JOIN avocado.data_service sv ON p.id = sv.program_id
            WHERE sv.fsid IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        products AS (
          SELECT route_id, ARRAY_AGG(product) AS product_ids
          FROM (
            SELECT DISTINCT route_id, product
            FROM (
              SELECT DISTINCT s.route_id, 'product:' || ssl.sku AS product, ssl.id
              FROM snapshots s
              JOIN avocado.data_shipmentsnapshotline ssl ON s.id = ssl.shipment_snapshot_id
              WHERE ssl.sku IS NOT NULL
            ) x
          ) d
          GROUP BY route_id
        ),
        batches AS (
          SELECT route_id, ARRAY_AGG(batch) AS batch_ids
          FROM (
            SELECT DISTINCT s.route_id, ssld.batch
            FROM snapshots s
            JOIN avocado.data_shipmentsnapshotline ssl ON s.id = ssl.shipment_snapshot_id
            JOIN avocado.data_shipmentsnapshotline_details ssld ON ssl.id = ssld.shipmentsnapshot_line
            WHERE ssld.batch IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        changes AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS change_doc_ids
          FROM (
            SELECT DISTINCT s.route_id, sc.couchdb_id
            FROM snapshots s
            JOIN ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc ON sc.snapshot_couchdb_id = s.couchdb_id
            WHERE sc.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        shipments AS (
          SELECT DISTINCT route_id, shipment_id
          FROM snapshots
          WHERE shipment_id IS NOT NULL
        ),
        otps AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS otp_doc_ids
          FROM (
            SELECT DISTINCT sh.route_id, so.couchdb_id
            FROM shipments sh
            JOIN avocado.shipment_otp so ON so.shipment_id = sh.shipment_id
            WHERE so.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        comments AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS comment_doc_ids
          FROM (
            SELECT DISTINCT sh.route_id, sc.couchdb_id
            FROM shipments sh
            JOIN ${DATA_SHIPMENT_COMMENT_TABLE_NAME} sc ON sc.shipment_id = sh.shipment_id
            WHERE sc.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        )
        SELECT r.route_id,
               r.carrier_id,
               r.last_updated,
               c.couchdb_ids,
               o.origin_ids,
               d.destination_ids,
               f.funder_ids,
               sv.service_ids,
               p.product_ids,
               b.batch_ids,
               ch.change_doc_ids,
               ot.otp_doc_ids,
               cm.comment_doc_ids
        FROM routes r
        LEFT JOIN couchdb c USING (route_id)
        LEFT JOIN origins o USING (route_id)
        LEFT JOIN destinations d USING (route_id)
        LEFT JOIN funders f USING (route_id)
        LEFT JOIN services sv USING (route_id)
        LEFT JOIN products p USING (route_id)
        LEFT JOIN batches b USING (route_id)
        LEFT JOIN changes ch USING (route_id)
        LEFT JOIN otps ot USING (route_id)
        LEFT JOIN comments cm USING (route_id)`

      const result = await client.query(query, params)

      const routeMap = {}
      for (const row of result.rows) {
        routeMap[row.route_id] = {
          carrierId: row.carrier_id || null,
          couchdbIds: row.couchdb_ids || [],
          commentDocIds: row.comment_doc_ids || [],
          locationIds: Array.from(new Set([
            ...(row.origin_ids || []),
            ...(row.destination_ids || [])
          ])),
          funderIds: row.funder_ids || [],
          serviceIds: row.service_ids || [],
          productIds: row.product_ids || [],
          changeDocIds: row.change_doc_ids || [],
          otpDocIds: row.otp_doc_ids || [],
          lastUpdated: row.last_updated,
          batchIds: row.batch_ids || []
        }
      }

      await client.query('COMMIT')
      return routeMap
    } catch (error) {
      if (client) await client.query('ROLLBACK').catch(() => {})
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-grouped-query-failed',
          cutoffDate,
          error: error.message
        })
      }
      throw error
    } finally {
      if (isPool && client) client.release()
    }
  }

  /**
   * Get LMD snapshot enrichment data from PostgreSQL.
   *
   * @param {string[]} shipmentIds - Array of shipment_id values
   * @returns {Promise<Object[]>} Array of enrichment objects keyed by shipment_id
   */
  async getLmdSnapshotData (shipmentIds) {
    if (!shipmentIds.length) return []

    const snapshotQuery = `
      SELECT
        ss.shipment_id,
        ss.couchdb_id,
        ss.status,
        ss.created_at,
        ss.created_by,
        ss.parent_doc_id,
        ss.raw_order_id,
        ss.is_gps_authenticated,
        ss.gps_validation_fail_comment,
        ss.gps_validation,
        (
          SELECT jsonb_object_agg(
            ssld.batch,
            jsonb_build_object('quantity', ssld.amount)
          )
          FROM avocado.data_shipmentsnapshotline ssl
          JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
          WHERE ssl.shipment_snapshot_id = ss.id
        ) AS counts
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      WHERE ss.shipment_id = ANY($1)
      ORDER BY ss.shipment_id, ss.created_at ASC
    `

    const otpQuery = `
      SELECT
        so.shipment_id,
        so.password,
        so.otp_key,
        so.user_fail_reason,
        so.verified
      FROM avocado.shipment_otp so
      WHERE so.shipment_id = ANY($1)
    `

    const [snapshotResult, otpResult] = await Promise.all([
      this.pgConnection.query(snapshotQuery, [shipmentIds]),
      this.pgConnection.query(otpQuery, [shipmentIds])
    ])

    const otpByShipment = {}
    for (const row of otpResult.rows) {
      otpByShipment[row.shipment_id] = row
    }

    const shipmentMap = {}
    for (const row of snapshotResult.rows) {
      const entry = shipmentMap[row.shipment_id] || { snapshotDates: {}, history: {} }
      shipmentMap[row.shipment_id] = entry

      const createdAtISO = row.created_at.toISOString()

      // snapshotDates: earliest date wins per status
      if (!entry.snapshotDates[row.status] || createdAtISO < entry.snapshotDates[row.status]) {
        entry.snapshotDates[row.status] = createdAtISO
      }

      // history: keyed by couchdb_id
      if (row.couchdb_id) {
        entry.history[row.couchdb_id] = omitBy({
          createdAt: createdAtISO,
          createdBy: { user: row.created_by, agent: '' },
          counts: row.counts || {},
          parentDocId: row.parent_doc_id
        }, isNil)
      }

      entry.orderId = row.raw_order_id
      entry.isGpsAuthenticated = row.is_gps_authenticated
      entry.gpsValidationFailComment = row.gps_validation_fail_comment
      entry.gpsValidation = row.gps_validation

      if (row.status === SHIPMENT_STATUS.RECEIVED) {
        entry.driverName = row.created_by
      }
    }

    return shipmentIds.map(shipmentId => {
      const entry = shipmentMap[shipmentId]
      if (!entry) return null

      const otpRow = otpByShipment[shipmentId]
      if (otpRow) {
        entry.otp = buildOtp(otpRow)
      }

      return { id: shipmentId, ...entry }
    }).filter(Boolean)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getChangeDocIds (snapshotCouchdbIds) {
    if (!Array.isArray(snapshotCouchdbIds) || snapshotCouchdbIds.length === 0) {
      return []
    }

    const query = `
      SELECT sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    try {
      const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
      return result.rows.map(row => row.couchdb_id)
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-change-query-failed',
          snapshotCouchdbIds,
          error: error.message
        })
      }
      throw error
    }
  }
}

module.exports = ShipmentSnapshotAdapter
